import { observer } from 'mobx-react';
import { MaterialReactTable } from 'material-react-table';
import { useEffect, useMemo, useState, forwardRef } from 'react';
import { Box } from '@mui/material';
import updateUrlParams from '../../hooks/updateUrlParams';
import useURLPaginate from '../../hooks/useURLPaginate';
import convertSchemaToColumns from './lib/convertSchemaToColumns';
import PAGINATION from './constants/pagination';
import { useLocalization } from '../../providers/LocalizationProvider';
import { useStore } from '../../providers/MobxProvider';
import LoggerUtil from '../../helpers/LoggerUtil';

const FormList = forwardRef(
  (
    {
      dummyData = null,
      columnPrimaryId,
      schema,
      dataProvider,
      initialState,
      listApiUrl,
      accessorFunctions = {},
      onRowCountChange = () => undefined,
      accessorFunctionsAdditional = {},
      children = null,
      onApiResponse = () => undefined,
      stickyColumn = false,
      sortBy,
      sortOrder,
      responseFormatter = null,
      enableBottomToolbar = true,
      rowsPerPageOptions = null,
    },
    ref,
  ) => {
    const { formatMessage } = useLocalization();
    const store = useStore();

    const { page, filters, limit, sorting } = useURLPaginate(PAGINATION);

    const { get } = dataProvider;

    // data and fetching state
    const [tableData, setTableData] = useState([]);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);

    // table state
    const [rowCount, setRowCount] = useState(0);
    const [sortingState, setSortingState] = useState(
      !sortBy
        ? []
        : [
            {
              desc: sortOrder === 'DESC',
              id: sortBy,
            },
          ],
    );
    const [pagination, setPagination] = useState({
      pageIndex: page || PAGINATION.PAGE_INDEX,
      pageSize: limit || PAGINATION.PAGE_SIZE,
    });

    const columns = useMemo(() =>
      convertSchemaToColumns(
        schema,
        formatMessage,
        accessorFunctions,
        accessorFunctionsAdditional,
        stickyColumn,
      ),
    );

    async function fetchData() {
      if (!tableData.length) {
        setIsLoading(true);
      } else {
        setIsRefetching(true);
      }

      const apiUrl = new URL(listApiUrl);

      apiUrl.searchParams.set('page', `${pagination.pageIndex + 1}`);
      apiUrl.searchParams.set('limit', `${pagination.pageSize}`);
      if (sortingState[0]?.id) {
        apiUrl.searchParams.set('sortBy', `${sortingState[0].id}`);
        apiUrl.searchParams.set(
          'sortOrder',
          `${sortingState[0].desc ? 'DESC' : 'ASC'}`,
        );
      }

      if (filters && Object.keys(filters).length > 0) {
        Object.entries(filters).forEach(([key, value]) => {
          apiUrl.searchParams.set(key, value);
        });
      }

      try {
        const apiResponse = await get(apiUrl.href);
        onApiResponse(apiResponse);
        if (apiResponse?.ok) {
          const response = apiResponse.data;
          let tableDataResponse = response.data;
          if (responseFormatter) {
            tableDataResponse = responseFormatter(tableDataResponse);
          }
          setTableData(tableDataResponse);
          const tempRowCount =
            response?.total || response?.totalItems || response?.data.length;
          setRowCount(tempRowCount);
          const updates = {
            page: pagination.pageIndex,
            limit: pagination.pageSize,
            filters,
            sorting,
          };

          const currentUrl = updateUrlParams(updates);
          window.history.replaceState({}, '', currentUrl);
        } else if (dummyData) {
          setTableData(dummyData);
        }
      } catch (error) {
        setIsError(true);
        LoggerUtil.log('List API Error', error);
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    }

    useEffect(() => {
      fetchData();
    }, [
      pagination.pageIndex,
      pagination.pageSize,
      filters,
      page,
      limit,
      JSON.stringify(sortingState),
      listApiUrl,
    ]);

    useEffect(() => {
      onRowCountChange(rowCount);
    }, [rowCount]);

    return (
      <Box ref={ref}>
        <MaterialReactTable
          autoResetPageIndex={false}
          localization={store.domain.locale.getForMuiTable()}
          columns={columns}
          data={tableData}
          getRowId={row => row[columnPrimaryId]}
          rowCount={rowCount}
          manualFiltering
          enableTopToolbar={false}
          enableBottomToolbar={enableBottomToolbar}
          manualPagination
          manualSorting
          enableColumnActions={false}
          onPaginationChange={setPagination}
          onSortingChange={updater => {
            // TODO: remove after version upgrade
            const newSorting =
              typeof updater === 'function' ? updater(sortingState) : updater;

            setSortingState(newSorting);
          }}
          enableColumnFilters={false}
          enableSorting
          enableFilters={false}
          initialState={initialState}
          state={{
            globalFilter: '',
            isLoading,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
            sorting: sortingState,
            pagination,
          }}
          muiToolbarAlertBannerProps={
            isError
              ? {
                  color: 'error',
                  children: formatMessage({
                    id: 'ERROR_LOADING_DATA',
                    defaultMessage: 'Error loading data',
                  }),
                }
              : undefined
          }
          muiTablePaperProps={{ elevation: 0 }}
          {...(rowsPerPageOptions !== null && {
            muiPaginationProps: {
              rowsPerPageOptions,
            },
          })}
          muiBottomToolbarProps={{
            sx: {
              minHeight: '2.5em',
            },
          }}
        />
        {children}
      </Box>
    );
  },
);

export default observer(FormList);
